import { useEffect, useRef, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  DefaultEntityFilters,
  EntityFilter,
  EntityKindPicker,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityTypePicker,
  useEntityList,
  UserListPicker,
} from '@backstage/plugin-catalog-react';

type GroupsFilters = DefaultEntityFilters & {
  groupOwnership?: GroupOwnershipEntityFilter;
};

class GroupOwnershipEntityFilter implements EntityFilter {
  constructor(private readonly allowedGroups: string[]) {}

  filterEntity(entity: Entity): boolean {
    const isUniversalTemplate =
      entity.metadata?.annotations?.['xvia.backstage.io/universal-template'] ===
      'true';

    if (isUniversalTemplate) {
      return true;
    }

    const kind = entity.kind?.toLowerCase();

    if (kind === 'group') {
      const groupName = entity.metadata?.name?.toLowerCase() ?? '';
      if (groupName.includes('deletion_scheduled')) {
        return false;
      }
    }

    if (kind === 'user' || kind === 'group') {
      return true;
    }

    if (!this.allowedGroups || this.allowedGroups.length === 0) {
      return true;
    }

    const entityOwner = entity.spec?.owner?.toString() ?? '';

    if (!entityOwner) {
      return false;
    }

    return this.allowedGroups.some(allowedGroup => {
      const normalizedOwner = entityOwner.toLowerCase();
      const normalizedAllowed = allowedGroup.toLowerCase();

      return (
        normalizedOwner === normalizedAllowed ||
        normalizedOwner.endsWith(`/${normalizedAllowed.split('/').pop()}`)
      );
    });
  }

  getCatalogFilters(): Record<string, string | string[]> {
    return {};
  }
}

export const GroupsPicker = () => {
  const [userOwnershipEntities, setUserOwnershipEntities] = useState<string[]>(
    [],
  );
  const identityApi = useApi(identityApiRef);
  const { filters, updateFilters } = useEntityList<GroupsFilters>();
  const lastAppliedFilterRef = useRef<string>('');

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      const ownershipEntities = identity.ownershipEntityRefs ?? [];
      setUserOwnershipEntities(ownershipEntities);
    });
  }, [identityApi]);

  useEffect(() => {
    const currentFilterKey = `auto:${JSON.stringify(userOwnershipEntities)}`;

    if (lastAppliedFilterRef.current === currentFilterKey) {
      return;
    }

    lastAppliedFilterRef.current = currentFilterKey;

    if (userOwnershipEntities.length > 0) {
      updateFilters({
        groupOwnership: new GroupOwnershipEntityFilter(userOwnershipEntities),
      });
    } else {
      if (filters.groupOwnership) {
        updateFilters({
          groupOwnership: undefined,
        });
      }
    }
  }, [filters.groupOwnership, userOwnershipEntities, updateFilters]);

  return (
    <>
      <EntityKindPicker />
      <EntityTypePicker />
      <UserListPicker
        availableFilters={['owned', 'starred']}
        initialFilter="all"
      />
      <EntityOwnerPicker />
      <EntityNamespacePicker />
    </>
  );
};
